// required for the model viewer
import * as AR from '@dora/arctic';
import '@google/model-viewer';
import { ModelViewerElement } from '@google/model-viewer';
import * as O from 'fp-ts/Option';
import * as H from 'preact/hooks';
import { createModelViewerElement } from './element';

export enum ModelViewerError {
    CONTEXT_LOST = 'CONTEXT_LOST',
    LOAD_FAILURE = 'LOAD_FAILURE',
    UNKNOWN = 'UNKNOWN',
}

interface ModelViewerResponse {
    // TODO: do data URLs need to be handled in some different way?
    /** trigger the model viewer, passing the model and a placement */
    activate: () => void;
    loading: boolean;
    error: string | null;
    canActivateAr: boolean;
    generateUSDZ: () => Promise<string>;
}

/**
 * Construct a ModelViewer which, when activated, displays a model in AR.
 *
 * This function is only expected to be used a single time. If it is used more, then multiple model
 * viewers will be added into the DOM
 */
export const useModelViewer = (
    src: Promise<O.Option<string>>,
    placement?: AR.Placement,
): ModelViewerResponse => {
    const modelViewerElement = H.useRef<undefined | ModelViewerElement>();
    const [progress, setProgress] = H.useState<number>(0);
    const [error, setError] = H.useState<ModelViewerError | null>(null);
    const [canActivateAr, setCanActivateAr] = H.useState(false);
    const [urlLoaded, setUrlLoaded] = H.useState<boolean>(false);

    H.useEffect(() => {
        modelViewerElement.current = createModelViewerElement();
        const modelViewer = modelViewerElement.current;

        const target = document.body;
        target.appendChild(modelViewer);

        modelViewer.addEventListener('progress', (event: any) => {
            setProgress((event?.detail?.totalProgress as number) ?? 1);
            setError(null);
            setCanActivateAr(modelViewer.canActivateAR);
        });

        modelViewer.addEventListener('error', (event: any) => {
            switch (event?.detail?.type) {
                case 'webglcontextlost':
                    setError(ModelViewerError.CONTEXT_LOST);
                    break;
                case 'loadfailure':
                    setError(ModelViewerError.LOAD_FAILURE);
                    break;
                default:
                    setError(ModelViewerError.UNKNOWN);
                    break;
            }
        });

        return () => {
            modelViewerElement.current?.remove();
            modelViewerElement.current = undefined;
        };
    }, []);

    const setValues = (currSrc?: string, placement?: string) => {
        if (
            modelViewerElement.current !== undefined &&
            currSrc !== undefined &&
            placement !== undefined
        ) {
            try {
                modelViewerElement.current.src = currSrc;
                modelViewerElement.current.arPlacement = placement;
            } catch (e) {
                alert(e);
            }
        }
    };

    const activate = H.useCallback(() => {
        modelViewerElement.current?.activateAR();
    }, [src, placement]);

    const generateUSDZ: () => Promise<string> = H.useCallback(() => {
        // this is not part of the public API ...
        return (modelViewerElement.current as any).prepareUSDZ();
    }, [src, placement]);

    H.useEffect(() => {
        src.then(
            O.map(_src => {
                setValues(_src, placement);
                setUrlLoaded(true);
            }),
        );
    }, [src, placement]);

    return {
        activate,
        generateUSDZ,
        error,
        loading: !urlLoaded || progress !== 1,
        canActivateAr,
    };
};
